import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { profilesService } from '@/services/profiles.service';
import type { UserProfile } from '@/services/profiles.service';

export default function ProfileSettingsPage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    hourlyRate: '',
    timezone: '',
    // Company Information
    companyName: '',
    companyAddress: '',
    tradeLicenseNumber: '',
    vatNumber: '',
    taxId: '',
    // Contact Details
    phone: '',
    website: '',
    // Payment Information
    bankName: '',
    bankAccountName: '',
    iban: '',
    swiftBic: '',
    paymentMethods: [] as string[],
    paymentInstructions: '',
    // Invoice Defaults
    defaultCurrency: '',
    defaultPaymentTerms: '',
    defaultTermsAndConditions: '',
    defaultTaxRate: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profilesService.getProfile();
      setProfile(data);
      setFormData({
        name: data.name,
        hourlyRate: data.hourlyRate?.toString() || '',
        timezone: data.timezone || '',
        companyName: data.companyName || '',
        companyAddress: data.companyAddress || '',
        tradeLicenseNumber: data.tradeLicenseNumber || '',
        vatNumber: data.vatNumber || '',
        taxId: data.taxId || '',
        phone: data.phone || '',
        website: data.website || '',
        bankName: data.bankName || '',
        bankAccountName: data.bankAccountName || '',
        iban: data.iban || '',
        swiftBic: data.swiftBic || '',
        paymentMethods: data.paymentMethods || [],
        paymentInstructions: data.paymentInstructions || '',
        defaultCurrency: data.defaultCurrency || '',
        defaultPaymentTerms: data.defaultPaymentTerms || '',
        defaultTermsAndConditions: data.defaultTermsAndConditions || '',
        defaultTaxRate: data.defaultTaxRate?.toString() || '',
      });
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentMethodToggle = (method: string) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.includes(method)
        ? prev.paymentMethods.filter((m) => m !== method)
        : [...prev.paymentMethods, method],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updateData: any = {
        id: 'PROFILE', // Required for DynamoDB composite key
      };
      if (formData.name) updateData.name = formData.name;
      if (formData.hourlyRate) updateData.hourlyRate = parseFloat(formData.hourlyRate);
      if (formData.timezone) updateData.timezone = formData.timezone;
      if (formData.companyName) updateData.companyName = formData.companyName;
      if (formData.companyAddress) updateData.companyAddress = formData.companyAddress;
      if (formData.tradeLicenseNumber) updateData.tradeLicenseNumber = formData.tradeLicenseNumber;
      if (formData.vatNumber) updateData.vatNumber = formData.vatNumber;
      if (formData.taxId) updateData.taxId = formData.taxId;
      if (formData.phone) updateData.phone = formData.phone;
      if (formData.website) updateData.website = formData.website;
      if (formData.bankName) updateData.bankName = formData.bankName;
      if (formData.bankAccountName) updateData.bankAccountName = formData.bankAccountName;
      if (formData.iban) updateData.iban = formData.iban;
      if (formData.swiftBic) updateData.swiftBic = formData.swiftBic;
      if (formData.paymentMethods.length > 0) updateData.paymentMethods = formData.paymentMethods;
      if (formData.paymentInstructions)
        updateData.paymentInstructions = formData.paymentInstructions;
      if (formData.defaultCurrency) updateData.defaultCurrency = formData.defaultCurrency;
      if (formData.defaultPaymentTerms)
        updateData.defaultPaymentTerms = formData.defaultPaymentTerms;
      if (formData.defaultTermsAndConditions)
        updateData.defaultTermsAndConditions = formData.defaultTermsAndConditions;
      if (formData.defaultTaxRate) updateData.defaultTaxRate = parseFloat(formData.defaultTaxRate);

      const updatedProfile = await profilesService.updateProfile(updateData);
      setProfile(updatedProfile);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    if (profile) {
      setFormData({
        name: profile.name,
        hourlyRate: profile.hourlyRate?.toString() || '',
        timezone: profile.timezone || '',
        companyName: profile.companyName || '',
        companyAddress: profile.companyAddress || '',
        tradeLicenseNumber: profile.tradeLicenseNumber || '',
        vatNumber: profile.vatNumber || '',
        taxId: profile.taxId || '',
        phone: profile.phone || '',
        website: profile.website || '',
        bankName: profile.bankName || '',
        bankAccountName: profile.bankAccountName || '',
        iban: profile.iban || '',
        swiftBic: profile.swiftBic || '',
        paymentMethods: profile.paymentMethods || [],
        paymentInstructions: profile.paymentInstructions || '',
        defaultCurrency: profile.defaultCurrency || '',
        defaultPaymentTerms: profile.defaultPaymentTerms || '',
        defaultTermsAndConditions: profile.defaultTermsAndConditions || '',
        defaultTaxRate: profile.defaultTaxRate?.toString() || '',
      });
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Breadcrumb items={[{ label: 'Settings', href: '/settings' }, { label: 'Profile' }]} />
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary"></div>
          <p className="text-gray-600 mt-4">Loading profile...</p>
        </div>
      </AppLayout>
    );
  }

  if (error && !profile) {
    return (
      <AppLayout>
        <Breadcrumb items={[{ label: 'Settings', href: '/settings' }, { label: 'Profile' }]} />
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg
            className="w-16 h-16 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error loading profile</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadProfile}
            className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Settings', href: '/settings' }, { label: 'Profile' }]} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
            <p className="text-gray-600">
              Manage your personal information, company details, and invoice settings
            </p>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="border border-border rounded-lg p-4 p-4 border-l-4 border-red-500">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Error</h3>
                <p className="text-gray-600 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email (read-only) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="mt-1 text-sm text-gray-500">Email cannot be changed (Google OAuth)</p>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="John Doe"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="+971 50 123 4567"
              />
            </div>

            {/* Hourly Rate */}
            <div>
              <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate (€/hour)
              </label>
              <input
                id="hourlyRate"
                name="hourlyRate"
                type="number"
                min="0"
                step="0.01"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="75.00"
              />
            </div>

            {/* Timezone */}
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select timezone</option>
                <option value="Asia/Dubai">Dubai (GST/UTC+4)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
              </select>
            </div>

            {/* Website */}
            <div className="md:col-span-2">
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="https://www.example.com"
              />
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div className="md:col-span-2">
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Company Name"
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty if you're a solo freelancer (will use your name on invoices)
              </p>
            </div>

            {/* Company Address */}
            <div className="md:col-span-2">
              <label
                htmlFor="companyAddress"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Business Address
              </label>
              <textarea
                id="companyAddress"
                name="companyAddress"
                rows={3}
                value={formData.companyAddress}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Business Address"
              />
            </div>

            {/* Trade License Number */}
            <div>
              <label
                htmlFor="tradeLicenseNumber"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Trade License Number
              </label>
              <input
                id="tradeLicenseNumber"
                name="tradeLicenseNumber"
                type="text"
                value={formData.tradeLicenseNumber}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="1234567890"
              />
            </div>

            {/* VAT Number */}
            <div>
              <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-2">
                VAT Number (if registered)
              </label>
              <input
                id="vatNumber"
                name="vatNumber"
                type="text"
                value={formData.vatNumber}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="1234567890123"
              />
            </div>

            {/* Tax ID */}
            <div className="md:col-span-2">
              <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-2">
                Tax ID / Registration Number
              </label>
              <input
                id="taxId"
                name="taxId"
                type="text"
                value={formData.taxId}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Tax ID"
              />
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bank Name */}
            <div>
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
                Bank Name
              </label>
              <input
                id="bankName"
                name="bankName"
                type="text"
                value={formData.bankName}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Emirates NBD"
              />
            </div>

            {/* Bank Account Name */}
            <div>
              <label
                htmlFor="bankAccountName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Account Name
              </label>
              <input
                id="bankAccountName"
                name="bankAccountName"
                type="text"
                value={formData.bankAccountName}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Account Name"
              />
            </div>

            {/* IBAN */}
            <div>
              <label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-2">
                IBAN
              </label>
              <input
                id="iban"
                name="iban"
                type="text"
                value={formData.iban}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="AE070331234567890123456"
              />
            </div>

            {/* SWIFT/BIC */}
            <div>
              <label htmlFor="swiftBic" className="block text-sm font-medium text-gray-700 mb-2">
                SWIFT/BIC Code
              </label>
              <input
                id="swiftBic"
                name="swiftBic"
                type="text"
                value={formData.swiftBic}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="EBILAEAD"
              />
            </div>

            {/* Payment Methods */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accepted Payment Methods
              </label>
              <div className="flex flex-wrap gap-3">
                {['bank_transfer', 'paypal', 'wise', 'revolut', 'stripe'].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => isEditing && !saving && handlePaymentMethodToggle(method)}
                    disabled={!isEditing || saving}
                    className={`px-4 py-2 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      formData.paymentMethods.includes(method)
                        ? 'border-flowky-primary bg-flowky-primary/10 text-flowky-primary font-medium'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {method === 'bank_transfer' && 'Bank Transfer'}
                    {method === 'paypal' && 'PayPal'}
                    {method === 'wise' && 'Wise'}
                    {method === 'revolut' && 'Revolut'}
                    {method === 'stripe' && 'Stripe'}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="md:col-span-2">
              <label
                htmlFor="paymentInstructions"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Payment Instructions
              </label>
              <textarea
                id="paymentInstructions"
                name="paymentInstructions"
                rows={4}
                value={formData.paymentInstructions}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Please make payment via bank transfer to the account details above. Include the invoice number in the payment reference."
              />
            </div>
          </div>
        </div>

        {/* Invoice Defaults */}
        <div className="border border-border rounded-lg p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Invoice Defaults</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Default Currency */}
            <div>
              <label
                htmlFor="defaultCurrency"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Default Currency
              </label>
              <select
                id="defaultCurrency"
                name="defaultCurrency"
                value={formData.defaultCurrency}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select currency</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="AED">AED - UAE Dirham</option>
                <option value="GBP">GBP - British Pound</option>
              </select>
            </div>

            {/* Default Tax Rate */}
            <div>
              <label
                htmlFor="defaultTaxRate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Default Tax Rate (%)
              </label>
              <input
                id="defaultTaxRate"
                name="defaultTaxRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.defaultTaxRate}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="5"
              />
              <p className="mt-1 text-sm text-gray-500">UAE VAT is typically 5%</p>
            </div>

            {/* Default Payment Terms */}
            <div className="md:col-span-2">
              <label
                htmlFor="defaultPaymentTerms"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Default Payment Terms
              </label>
              <input
                id="defaultPaymentTerms"
                name="defaultPaymentTerms"
                type="text"
                value={formData.defaultPaymentTerms}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Payment due within 30 days of invoice date"
              />
            </div>

            {/* Default Terms and Conditions */}
            <div className="md:col-span-2">
              <label
                htmlFor="defaultTermsAndConditions"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Default Terms and Conditions
              </label>
              <textarea
                id="defaultTermsAndConditions"
                name="defaultTermsAndConditions"
                rows={6}
                value={formData.defaultTermsAndConditions}
                onChange={handleInputChange}
                disabled={!isEditing || saving}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder={`Payment is due within 30 days of invoice date.
Late payments may incur additional charges.
Please include the invoice number with your payment.`}
              />
              <p className="mt-1 text-sm text-gray-500">
                These terms will be pre-filled when creating new invoices
              </p>
            </div>
          </div>
        </div>

        {/* Account Info */}
        {profile?.createdAt && (
          <div className="border border-border rounded-lg p-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Created
                </label>
                <input
                  type="text"
                  value={new Date(profile.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  disabled
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
